import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getApiUsage,
  getExtractorHealth,
  getPoolStats,
  listCreatorDataFlags,
  listCreatorDataRequests,
  resolveCreatorDataFlag,
  resolveCreatorDataRequest,
} from '../../lib/adminApi';
import { adminKeys } from './adminQueryKeys';

/**
 * Short stale time on purpose. This page is read when someone suspects the extraction layer is
 * broken, and a five-minute-old success rate is exactly the wrong thing to show them.
 */
const DATA_SOURCES_STALE_MS = 30 * 1000;

export function useExtractorHealth(hours: number) {
  return useQuery({
    queryKey: adminKeys.dataSources.extractors(hours),
    queryFn: () => getExtractorHealth(hours),
    placeholderData: keepPreviousData,
    staleTime: DATA_SOURCES_STALE_MS,
  });
}

export function useCreatorDataFlags(filters: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: adminKeys.dataSources.flags(filters as Record<string, unknown>),
    queryFn: () => listCreatorDataFlags(filters),
    placeholderData: keepPreviousData,
    staleTime: DATA_SOURCES_STALE_MS,
  });
}

export function useApiUsage(days: number) {
  return useQuery({
    queryKey: adminKeys.dataSources.apiUsage(days),
    queryFn: () => getApiUsage(days),
    placeholderData: keepPreviousData,
    staleTime: DATA_SOURCES_STALE_MS,
  });
}

export function usePoolStats() {
  return useQuery({
    queryKey: adminKeys.dataSources.pool,
    queryFn: getPoolStats,
    staleTime: DATA_SOURCES_STALE_MS,
  });
}

/**
 * Creator removal requests.
 *
 * No `placeholderData` on purpose, unlike the lists above: a stale row here means an admin could
 * apply a removal against a request that has since been verified automatically, and seeing a brief
 * spinner is cheaper than acting on a row that is no longer true.
 */
export function useCreatorDataRequests(filters: { status?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: adminKeys.dataSources.creatorRequests(filters as Record<string, unknown>),
    queryFn: () => listCreatorDataRequests(filters),
    staleTime: DATA_SOURCES_STALE_MS,
  });
}

export function useResolveCreatorDataRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      notes,
    }: {
      id: string;
      status: 'APPLIED' | 'REJECTED';
      notes?: string;
    }) => resolveCreatorDataRequest(id, status, notes),
    onSuccess: () => {
      // Applying a request deletes pooled readings, so the pool counts on this same page are now
      // wrong too.
      void queryClient.invalidateQueries({ queryKey: adminKeys.dataSources.all });
    },
  });
}

export function useResolveCreatorDataFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACCEPTED' | 'REJECTED' | 'QUARANTINED' }) =>
      resolveCreatorDataFlag(id, status),
    onSuccess: () => {
      // Quarantining also changes the pool counts, so invalidate the whole domain rather than the
      // flag list alone.
      void queryClient.invalidateQueries({ queryKey: adminKeys.dataSources.all });
    },
  });
}
